import React, { useState, useEffect } from "react";
import CurrencyTextField from "@unicef/material-ui-currency-textfield";
import { Box, Typography, Dialog, DialogActions, DialogContent, DialogTitle, Button, Grid } from "@material-ui/core";
import { Club, getCompanyMemberBudget, MemberBudget, updateCompanyMemberBudget } from "api/clubs";
import { getMembers } from "api/admin/members";
import { Subscription } from "api/company";
import { View } from "features/global/reducer";
import { DefaultTable } from "components";
import { useSnackbar } from "notistack";
import { Column, Query, QueryResult } from "material-table";
import { useMounted } from "utils/hooks";
import { ErrorTypes, formatter } from "../../../utils/helpers";
import BulkEditDialog from "./MemberBulkEditDialog";
import AttentionBanner from "./AttentionBanner";
import "./b2bfeaturesStyle.scss";
import i18n from "./i18n";

// prop types
interface CompanySettingsTabProps {
    club: Club,
    subscription: Subscription | null,
    onNavigateTo: ({ view, tab }: { view: View, tab?: number }) => void,
}

// member club settings tab
const MemberSettingsTab = ({ club, subscription, onNavigateTo }: CompanySettingsTabProps) => {
    // ui state
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    const [isLoading, setLoading] = useState(false);
    const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);

    const [showSecondWarningDialog, setShowSecondWarningDialog] = useState(false);
    const [showFirstWarningDialog, setShowFirstWarningDialog] = useState(false);
    const [applyToCurrentMonth, setApplyToCurrentMonth] = useState(false);
    const [newMemberBudget, setNewMemberBudget] = useState<MemberBudget>();
    const [oldMemberBudget, setOldMemberBudget] = useState<MemberBudget>();
    const [editMemberBudgetOld, setEditMemberBudgetOld] = useState<MemberBudget | undefined>(undefined);
    const [editMemberBudgetNew, setEditMemberBudgetNew] = useState<MemberBudget | undefined>(undefined);
    const [showEditMemberBudgetDialog, setShowEditMemberBudgetDialog] = useState(false);

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshMembersTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange(); // execute query again
    };
    useEffect(refreshMembersTable, [club]);

    const onRowUpdate = (newData: MemberBudget, oldData?: MemberBudget) => new Promise<void>((resolve) => {
        setNewMemberBudget(newData);
        setOldMemberBudget(oldData);
        setShowFirstWarningDialog(true);
        resolve();
    });

    const firstWarningAction = async (applyToCurrentMonthFlag: boolean) => {
        setApplyToCurrentMonth(applyToCurrentMonthFlag);
        setShowFirstWarningDialog(false);
        const monthlyCompanyMemberBudgetCentsNew = (newMemberBudget?.monthlyCompanyMemberBudgetCents === undefined ? 0 : newMemberBudget.monthlyCompanyMemberBudgetCents);
        const monthlyCompanyMemberBudgetCentsOld = (oldMemberBudget?.monthlyCompanyMemberBudgetCents === undefined ? 0 : oldMemberBudget.monthlyCompanyMemberBudgetCents);

        if (monthlyCompanyMemberBudgetCentsNew >= monthlyCompanyMemberBudgetCentsOld) {
            setShowSecondWarningDialog(false);
            saveAction(true, applyToCurrentMonthFlag);
        } else {
            setShowSecondWarningDialog(true);
        }
    };
    const saveAction = async (applySaveAction: boolean, applyToCurrentMonthFlag: boolean) => {
        if (!applySaveAction) {
            setShowSecondWarningDialog(false);
            refreshMembersTable();
        } else {
            try {
                if (newMemberBudget) {
                    await updateCompanyMemberBudget({
                        clubId: club.id,
                        monthlyCompanyMemberBudgetCents: newMemberBudget.monthlyCompanyMemberBudgetCents || 0,
                        userIds: [newMemberBudget.user.id],
                        applyToCurrentMonth: applyToCurrentMonthFlag,
                    });
                }
            } catch (err) {
                displayError(err);
            } finally {
                refreshMembersTable();
                setShowSecondWarningDialog(false);
            }
        }
    };

    const tableActions = [
        {
            disabled: (subscription === null),
            icon: "gavel",
            isFreeAction: true,
            tooltip: i18n.bulkEdit,
            onClick: () => setShowBulkEditDialog(true),
        },
        (memberBudget: MemberBudget) => ({
            disabled: (subscription === null),
            icon: "edit",
            tooltip: i18n.edit,
            onClick: () => {
                setEditMemberBudgetOld(memberBudget);
                setEditMemberBudgetNew(memberBudget);
                setShowEditMemberBudgetDialog(true);
            },
        }),
    ];

    // table columns
    const tableColumns: Column<MemberBudget>[] = [
        {
            editable: "never",
            field: "firstName",
            render: (rowData: MemberBudget) => rowData.user.firstName,
            title: i18n.firstName,
        },
        {
            editable: "never",
            field: "lastName",
            render: (rowData: MemberBudget) => rowData.user.lastName,
            title: i18n.lastName,
        },
        {
            editable: "never",
            field: "user.email",
            sorting: false,
            title: i18n.emailAddress,
        },
        {
            editable: "never",
            field: "usedMemberBudgetCents",
            render: (rowData: MemberBudget) => formatter.format(rowData.usedMemberBudgetCents / 100),
            sorting: false,
            title: i18n.usedMemberBudget,
        },
        {
            field: "monthlyCompanyMemberBudgetCents",
            sorting: false,
            render: (rowData: MemberBudget) => (rowData.monthlyCompanyMemberBudgetCents ? formatter.format(rowData.monthlyCompanyMemberBudgetCents / 100) : formatter.format(0)),
            title: i18n.companyMemberBudget,
        },
        {
            editable: "never",
            field: "site",
            sorting: false,
            title: i18n.site,
        },
    ];

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<MemberBudget>) => {
        // paging
        const params: Parameters<typeof getCompanyMemberBudget>[0] = {
            clubId: club.id,
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // sorting
        if (query.orderBy) {
            params.sortOrder = query.orderDirection.toUpperCase() as typeof params["sortOrder"];
            params.sortKey = query.orderBy.field as typeof params["sortKey"];
        }

        // filter
        params.query = query.search;

        return params;
    };

    const defaultData = {
        data: [],
        page: 0,
        totalCount: 0,
    };

    const tableQuery = (query: Query<MemberBudget>): Promise<QueryResult<MemberBudget>> => new Promise((resolve) => {
        const params = convertQueryToParams(query);
        getCompanyMemberBudget(params)
            .then((response) => {
                getMembers({
                    clubId: club.id,
                    limit: query.pageSize,
                    offset: query.pageSize * query.page,
                    query: query.search,
                    sortOrder: query.orderBy ? query.orderDirection.toUpperCase() as "ASC" | "DESC" | undefined : undefined,
                    sortKey: query.orderBy ? query.orderBy.field as "firstName" | "lastName" | undefined : undefined,
                }).then((members) => {
                    const newResult: MemberBudget[] = [];
                    members.data.forEach((m) => {
                        const budget = response.data.filter((b) => b.user.id === m.id)[0];
                        newResult.push({
                            id: m.id,
                            clubName: m.clubName,
                            monthDate: budget?.monthDate || "00-00-0000",
                            totalMemberBudgetCents: budget?.totalMemberBudgetCents || 0,
                            usedMemberBudgetCents: budget?.usedMemberBudgetCents || 0,
                            user: {
                                email: m.email,
                                firstName: m.firstName,
                                lastName: m.lastName,
                                id: m.id,
                                isAlias: m.isAlias,
                                profilePictureUrl: m.profilePictureUrl,
                                mainAccount: m.mainAccount,
                            },
                            monthlyCompanyMemberBudgetCents: m.monthlyCompanyMemberBudgetCents,
                            site: m.site,
                        });
                    });
                    resolve({
                        data: newResult,
                        totalCount: members.totalCount,
                        page: query.page,
                    });
                    setLoading(false);
                }).catch((error) => {
                    displayError(error);
                    resolve(defaultData);
                });
            }).catch((error) => {
                if (error.type === ErrorTypes.CLUB_IN_DEMO_MODE) {
                    enqueueSnackbar(i18n.demoModeErrorMsg, { variant: "error" });
                } else {
                    displayError(error);
                }
                resolve(defaultData);
            });
    });

    // render
    return (
        <>
            {(subscription === null || club.clubStatus === "demo") && (
                <Box className="boxContainer">
                    <AttentionBanner club={club} onNavigateTo={onNavigateTo} />
                </Box>
            )}

            <>
                <DefaultTable
                    actions={tableActions}
                    columns={tableColumns}
                    data={tableQuery}
                    isLoading={isLoading}
                    tableRef={tableRef}
                    title={i18n.memberBudgets}
                />

                <BulkEditDialog
                    showDialog={showBulkEditDialog}
                    onClose={() => setShowBulkEditDialog(false)}
                    onRefreshMembersTable={refreshMembersTable}
                    club={club}
                />
            </>

            <Dialog open={showFirstWarningDialog}>
                <DialogTitle>{i18n.warningMsgTitle}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>{i18n.warningMsgForChangedBudget}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => firstWarningAction(false)}>{i18n.no}</Button>
                    <Button color="primary" onClick={() => firstWarningAction(true)}>{i18n.yes}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showSecondWarningDialog}>
                <DialogTitle>{i18n.warningMsgTitle}</DialogTitle>
                <DialogContent dividers>
                    <Grid container>
                        <Grid item xs={12}>
                            <Typography>{i18n.warningMsgForCompanyMemberBudgetChange}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => saveAction(false, applyToCurrentMonth)}>{i18n.cancel}</Button>
                    <Button color="primary" onClick={() => saveAction(true, applyToCurrentMonth)}>{i18n.confirm}</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Member Budget Dialog */}
            <Dialog open={showEditMemberBudgetDialog} onClose={() => setShowEditMemberBudgetDialog(false)}>
                <DialogTitle>{i18n.companyMemberBudget}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <CurrencyTextField
                                currencySymbol="€"
                                decimalCharacter=","
                                digitGroupSeparator="."
                                fullWidth
                                label={i18n.companyMemberBudget}
                                helperText=""
                                maximumValue="1000000"
                                minimumValue="0"
                                value={editMemberBudgetNew?.monthlyCompanyMemberBudgetCents !== undefined ? (editMemberBudgetNew?.monthlyCompanyMemberBudgetCents || 0) / 100 : 0}
                                variant="outlined"
                                onChange={(e: any, value: number) => setEditMemberBudgetNew({ ...editMemberBudgetNew!, monthlyCompanyMemberBudgetCents: value * 100 })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setShowEditMemberBudgetDialog(false)}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" onClick={() => onRowUpdate(editMemberBudgetNew!, editMemberBudgetOld!).then(() => setShowEditMemberBudgetDialog(false))}>
                        {i18n.confirm}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MemberSettingsTab;
